import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function Hero({
  title = "Default Title",
  subtitle = "",
  backgroundImage,
  backgroundImages = [],
  variant = "inner",
  overlayOpacity = "bg-black/10",
  children,
  slideInterval = 5000,
}) {
  const isHome = variant === "home";
  const [current, setCurrent] = useState(0);

  // Auto slide effect
  useEffect(() => {
    if (!isHome || backgroundImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % backgroundImages.length);
    }, slideInterval);

    return () => clearInterval(interval);
  }, [isHome, backgroundImages, slideInterval]);

  const activeImage =
    backgroundImages.length > 0
      ? backgroundImages[current]
      : backgroundImage;

  return (
    <section
      className={`relative overflow-hidden transition-all duration-700 ${isHome
        ? "min-h-[80vh] md:min-h-[750px]"
        : "min-h-[220px] md:min-h-[320px]"
        }`}
    >
      {/* Background Slider */}
      {isHome && backgroundImages.length > 0 && (
        <>
          {backgroundImages.map((img, index) => (
            <div
              key={index}
              className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
              style={{
                backgroundImage: `url(${img})`,
                opacity: index === current ? 1 : 0,
              }}
            />
          ))}
        </>
      )}

      {/* Fallback single image */}
      {!isHome && activeImage && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${activeImage})`,
          }}
        />
      )}

      {/* Overlay */}
      <div
        className={`absolute inset-0 ${isHome
          ? overlayOpacity
          : "bg-gradient-to-r from-black/50 via-black/30 to-black/50"
          }`}
      />

      {/* Content */}
      <div
        className={`relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-6 ${isHome
          ? "h-full flex items-center"
          : "min-h-[250px] md:min-h-[320px] flex items-center justify-center text-center"
          }`}
      >
        {isHome ? (
          <div className="w-full grid lg:grid-cols-2 gap-6 lg:gap-12 items-center py-16 sm:py-20 lg:py-24">
            <div className="text-white">
              <span className="inline-block px-4 py-2 mb-5 rounded-full bg-[#FF6B35]/20 border border-[#FF6B35]/40 text-sm font-medium">
                🇦🇪 Save up to 60% on Dubai Hotels
              </span>

              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold leading-tight">
                {title}
              </h1>

              {subtitle && (
                <p className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed max-w-2xl">
                  {subtitle}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8">
                <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-xl">
                  ✓ Best Prices
                </div>

                <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-xl">
                  ✓ Free Cancellation
                </div>

                <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-xl">
                  ✓ Instant Confirmation
                </div>
              </div>


              <div className="flex flex-wrap gap-2 sm:gap-4 mt-6 sm:mt-8">
                <Link
                  to="https://dubaicheaphotels.com/hotels"
                  className="border border-white hover:bg-white hover:text-black transition px-8 py-4 rounded-xl font-semibold inline-block"
                >
                  Today's Deals
                </Link>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="rounded-3xl shadow-2xl">
                {children}
              </div>
            </div>

            {children && (
              <div className="block lg:hidden mt-10">
                {children}
              </div>
            )}
          </div>
        ) : (
          <div className="text-white max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              {title}
            </h1>

            {subtitle && (
              <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
                {subtitle}
              </p>
            )}

            {children && <div className="mt-6">{children}</div>}
          </div>
        )}
      </div>
    </section>
  );
}